<script setup lang="ts">
import { LazyExtraDiscountModal } from '#components'

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: '/', // -> accessible at /en/ (with prefix)
    fr: '/', // -> accessible at /fr/ (with prefix)
  },
})
definePageMeta({
  name: 'IndexPage',
})

const userStore = useUserStore()
const config = useRuntimeConfig()
const localePath = useLocalePath()
const overlay = useOverlayWithAd()
const toast = useToast()
const modalStore = useModalStore()
const baseStore = useBaseStore()

const { data, pending, error } = await useFetch(`${config.public.apiUrl}/home/fetch-home`, {
  transform: (response) => {
    // Do anything you want here!
    // console.log(response)
    return response
  },
})

// Track recipe prints on client side when featured recipes are loaded
watch(
  data,
  (newData) => {
    if (import.meta.client && newData?.sections?.featured_recipes?.length > 0) {
      baseStore.trackRecipePrints(newData.sections.featured_recipes, 'home').catch(() => {
        // Silent fail - tracking shouldn't block the UI
      })
    }
  },
  { immediate: true },
)

const tools = [
  {
    label: 'Circulaires',
    icon: 'i-gridicons-tag',
    to: localePath({ name: 'FlyersPage' }),
    extra: false,
  },
  {
    label: 'Liste d\'épicerie',
    icon: 'i-material-symbols-light-list-alt-rounded',
    to: localePath({ name: 'ShoppingListPage' }),
    extra: false,
  },
  {
    label: 'L\'économiseur',
    icon: 'i-custom-sack-dollars',
    to: localePath({ name: 'MoneySaverPage' }),
    extra: true,
  },
  {
    label: 'Vide-frigo',
    icon: 'i-mdi-fridge',
    to: localePath({ name: 'RecipesPage' }),
    extra: false,
  },
  {
    label: 'Produits suivis',
    icon: 'i-fluent-tag-search-24-filled',
    to: localePath({ name: 'TrackedIngredientsPage' }),
    extra: true,
  },
  {
    label: 'Boîte de recettes',
    icon: 'i-material-symbols-box-rounded',
    to: localePath({ name: 'RecipeBoxPage' }),
    extra: false,
  },
  {
    label: 'Planificateur',
    icon: 'i-custom-tool-meal-planner',
    to: localePath({ name: 'MealPlannerPage' }),
    extra: true,
  },
  {
    label: 'Prix de l\'essence',
    icon: 'i-mdi-gas-station',
    to: localePath({ name: 'GasPricePage' }),
    extra: false,
  },
]

// defineEmits(['openDiscountModal'])
async function openDiscountModal(discount) {
  if (userStore.user) {
    const modal = overlay.create(LazyExtraDiscountModal, {
      props: { discount },
    })
    modal.open()
  }
  else {
    toast.add({
      title: 'Compte Glouton requis',
      color: 'error',
      description: `Ce rabais de ${discount.company_name} est réservé à nos membres seulement.`,
      actions: [{
        label: 'Connexion',
        color: 'secondary',
        variant: 'outline',
        onClick: (e) => {
          modalStore.openLoginModal()
        },
      }, {
        label: 'Créer un compte',
        color: 'secondary',
        variant: 'solid',
        to: localePath({ name: 'RegisterPage' }),
      }],
      icon: 'i-lucide-alert-circle',
    })
  }
}

const showWelcomeAlert = ref(true)
const route = useRoute()

function openPromoModalFromQuery() {
  if (typeof route.query.code !== 'string' || !route.query.code.trim())
    return
  nextTick(() => {
    modalStore.openPromoBannerModal()
  })
}

onMounted(() => {
  const isHidden = localStorage.getItem('hide_welcome_alert_bis')
  if (isHidden === 'true') {
    showWelcomeAlert.value = false
  }

  if (userStore.authChecked) {
    openPromoModalFromQuery()
  }
})

watch(() => userStore.authChecked, (checked) => {
  if (checked)
    openPromoModalFromQuery()
})

function closeWelcomeAlert() {
  showWelcomeAlert.value = false
  localStorage.setItem('hide_welcome_alert_bis', 'true')
}

const hasActivePromo = computed(() => !!baseStore.promos && baseStore.shouldShowPromos && baseStore.promoStatus === 'valid')

const promoPercentage = computed(() => {
  if (baseStore.promos?.percentage) {
    return Math.round(Number.parseFloat(baseStore.promos.percentage))
  }
  return 40
})

const promoBadgeBackground = computed(() => {
  if (baseStore.promos?.banner_bg_from && baseStore.promos?.bg_from_to) {
    return `linear-gradient(135deg, ${baseStore.promos.banner_bg_from} 0%, ${baseStore.promos.bg_from_to} 100%)`
  }
  return 'linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #ff4757 100%)'
})

const promoBadgeTextColor = computed(() => baseStore.promos?.title_color || '#FFFFFF')
</script>

<template>
  <UDashboardPanel id="IndexPage">
    <template #header />

    <template #body>
      <UContainer class="px-0 sm:px-0 py-0 flex flex-col ">
        <h1 class="hidden lg:block text-highlighted text-3xl font-bold sm:text-4xl font-title my-6 border-b border-poivre-100 pb-2">
          Économiser et bien manger, c'est notre recette.
        </h1>

        <ClientOnly>
          <UAlert
            v-if="showWelcomeAlert"
            class="rounded-none lg:rounded-lg xl:mb-3 "
            color="secondary"
            title="Économiser, c'est simple avec Glouton!"
            variant="solid"
            description="Trouve les meilleurs rabais, planifie tes repas, fais ta liste: Glouton rend l’épicerie simple et économique."
            icon="i-lucide-info"
            :actions="[
              {
                label: 'Comment ça fonctionne?',
                color: 'primary',
                variant: 'solid',
                class: 'text-xs lg:text-sm',
                to: localePath({ name: 'WelcomePage' }),
              },
              {
                label: 'Voir la vidéo',
                color: 'white',
                variant: 'soft',
                icon: 'i-mdi-play',
                class: 'text-xs lg:text-sm',
                onClick: () => modalStore.openVideoModal(),
              },
            ]"
            :ui="{
              icon: 'size-8',
              title: 'text-sm lg:text-base',
              description: 'text-sm lg:text-base',
              close: 'text-white',
            }"

            close
            @update:open="closeWelcomeAlert"
          />
        </ClientOnly>

        <div v-if="userStore.user" class="flex md:hidden items-center justify-between gap-3 w-full mb-0.5">
          <div class="text-base lg:text-lg text-poivre-800 ml-4">
            Bonjour, <span class="font-semibold uppercase">{{ userStore.user.firstname }}</span>
          </div>

          <div class=" flex items-center">
            <ULink
              v-if="userStore.user?.extra == 1"
              :to="localePath({ name: 'ExtraPage' })"
              class="index-member-extra flex items-center gap-2 rounded-bl-lg px-3 py-1.5  shadow-sm hover:border-amber-500/80 transition-all overflow-hidden relative"
              style="background: linear-gradient(135deg, #e5d9a2 0%, #f0e8be 40%, #f7f2df 50%, #f0e8be 60%, #e5d9a2 100%)"
            >
              <UIcon :name="userStore.user?.extra_active_trial === 1 ? 'i-mdi-timer-outline' : 'i-mdi-star'" class="size-5 text-tomate-600/90 " />
              <div class="text-left leading-tight flex flex-col gap-0">
                <span class="block text-sm font-bold leading-3 text-poivre-600">Membre Extra</span>
                <span v-if="userStore.user.extra_active_trial == 1" class="block text-xs font-normal opacity-90 leading-3 text-poivre-700">En période d'essai</span>
                <span v-else class="block text-xs font-normal opacity-90 leading-3 text-poivre-700">Tous les avantages</span>
              </div>
              <div class="index-member-extra-shine absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent pointer-events-none" />
            </ULink>
            <button
              v-else-if="hasActivePromo"
              type="button"
              class="flex items-center gap-2 rounded-bl-lg px-3 py-1.5 shadow-md overflow-hidden relative cursor-pointer text-left"
              :style="{ background: promoBadgeBackground, color: promoBadgeTextColor }"
              @click="modalStore.openPromoBannerModal()"
            >
              <UIcon name="i-mdi-percent" class="size-5 shrink-0" />
              <div class="text-left leading-tight flex flex-col gap-0">
                <span class="block text-sm font-bold leading-3">-{{ promoPercentage }}% sur l'Extra</span>
                <span class="block text-xs font-normal opacity-90 leading-3">Détails + commander</span>
              </div>
              <div class="index-member-extra-shine absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent pointer-events-none" />
            </button>
            <ULink
              v-else
              :to="localePath({ name: 'ExtraPage' })"
              class="flex items-center gap-2 rounded-bl-lg  px-3 py-1.5 border border-sorbet-400 bg-sorbet text-poivre-800 hover:bg-sorbet-200 hover:border-sorbet-500 hover:text-poivre-900 shadow-sm"
            >
              <UIcon name="i-mdi-star" class="size-5 text-tomate-500 shrink-0" />
              <div class="text-left leading-tight flex flex-col gap-0">
                <span class="block text-sm font-medium leading-3">Découvrir l'Extra</span>
                <span v-if="userStore.user.extra_active_trial == 0 && userStore.user.trial_status == 1" class="block text-xs font-normal opacity-90 leading-3">Encore plus d'économies</span>
                <span v-else class="block text-xs font-normal opacity-90 leading-3">Essai gratuit 14 jours</span>
              </div>
            </ULink>
          </div>
        </div>

        <div class="px-4 lg:px-0 mt-4  pb-2 mb-2 xl:mb-4 lg:border-b lg:border-poivre-100 flex justify-between items-center">
          <h3 class="text-base lg:text-2xl font-semibold lg:font-bold leading-6 text-poivre-900 lg:font-title uppercase lg:normal-case">
            Nos outils pour vous simplifier la vie
          </h3>
        </div>
        <div class="flex justify-between w-full overflow-hidden mb-4 xl:mb-8">
          <UCarousel
            v-slot="{ item }"
            :items="tools"
            class="w-full"
            :skip-snaps="true"
            :ui="{
              item: 'basis-28 md:basis-30 lg:basis-1/7 xl:basis-1/7 ps-2',
              viewport: 'overflow-visible xl:overflow-hidden px-5 pb-1',
            }"
          >
            <ULink
              class="group flex flex-col items-center relative"
              :to="item.to"
            >
              <div
                class="relative w-22 h-22 lg:w-30 lg:h-30 bg-white rounded-full flex items-center justify-center shadow"
                :class="item.extra ? 'border-2 border-transparent border-tomate' : 'border-2 border-transparent'"
              >
                <UIcon
                  :name="item.icon"
                  class="h-9 w-9 lg:h-12 lg:w-12 text-tomate-500 transition-transform duration-200 group-hover:scale-120"
                />
                <div
                  v-if="item.extra"
                  class="shadow absolute -bottom-1 text-xs px-1.5 py-0.5 rounded flex space-x-1 items-center"
                  style="background: linear-gradient(135deg, #e5d9a2 0%, #f0e8be 40%, #f7f2df 50%, #f0e8be 60%, #e5d9a2 100%);"
                >
                  <UIcon name="i-mdi-star" class="text-primary" /><div class="text-xs font-semibold text-poivre-600">
                    Extra
                  </div>
                </div>
              </div>

              <div class="w-full text-center font-medium lg:font-semibold mt-3 text-sm/4 md:text-sm/4 lg:text-base/4">
                {{ item.label }}
              </div>
            </ULink>
          </UCarousel>
        </div>

        <PromoBanner class="mb-6 mx-4 lg:mx-0" message-type="message_home" />

        <!-- Web : DisplayAd -->
        <DisplayAd
          v-if="baseStore.showAds && baseStore?.platform === 'web' && (!userStore?.user || userStore.user.extra != 1)"
          dfp-ad-unit-path="glouton/accueil"
          :sizes="[[970, 250], [728, 90], [320, 50], [300, 250]]"
          :size-mapping="[[[0, 0], [[320, 50], [300, 250]]], [[768, 0], [[728, 90]]], [[1024, 0], [[970, 250], [728, 90]]]]"
          custom-class="mb-6 mx-4 lg:mx-0"
        />

        <div class="px-4 lg:px-0 pb-2 mb-2 xl:mb-4 lg:border-b lg:border-poivre-100 flex justify-between items-center">
          <h3 class="text-base lg:text-2xl font-semibold lg:font-bold leading-6 text-poivre-900 lg:font-title uppercase lg:normal-case">
            Recettes en vedette
          </h3>
          <UButton size="sm" label="Toutes les recettes" color="primary" variant="solid" :to="localePath({ name: 'RecipesPage' })" />
        </div>
        <div class="flex justify-between w-full mb-12">
          <UCarousel
            v-slot="{ item }"
            :items="data?.sections?.featured_recipes"
            class="w-full"
            :skip-snaps="false"
            dots
            :ui="{
              item: 'basis-70 ps-0',
              viewport: ' px-4 pb-1 overflow-hidden px-4 lg:px-0',
              container: 'space-x-4 items-stretch px-4',
              dot: 'bg-tertiary-200 data-[state=active]:bg-tertiary-500 size-2',
            }"
            :prev="{ color: 'tertiary' }"
            :next="{ color: 'tertiary' }"
          >
            <RecipeCard
              class="flex flex-col grow h-full"
              :recipe="item"
            />
          </UCarousel>
        </div>

        <div class="px-4 lg:px-0 pb-2 mb-2 xl:mb-4 lg:border-b lg:border-poivre-100 flex justify-between items-center">
          <h3 class="text-base lg:text-2xl font-semibold lg:font-bold leading-6 text-poivre-900 lg:font-title uppercase lg:normal-case">
            Rabais partenaires
          </h3>
          <UButton size="sm" label="Tous les rabais" color="primary" variant="solid" :to="localePath({ name: 'ExtraDiscountsPage' })" />
        </div>
        <div class="flex justify-between w-full mb-12">
          <UCarousel
            v-slot="{ item }"
            :items="data?.sections?.discounts"
            class="w-full"
            :skip-snaps="false"
            dots
            :ui="{
              item: 'basis-50 ps-0',
              viewport: ' px-4 pb-1 overflow-hidden px-4 lg:px-0',
              container: 'space-x-4 items-stretch px-4',
              dot: 'bg-tertiary-200 data-[state=active]:bg-tertiary-500 size-2',
            }"
            :prev="{ color: 'tertiary' }"
            :next="{ color: 'tertiary' }"
          >
            <div class="rounded-lg border-dashed border-2 border-tomate bg-white p-3 h-full space-y-2 flex flex-col relative">
              <div
                v-if="item.type === 'EXTRA'"
                class="absolute top-0 right-0 items-center flex px-1.5 py-1 rounded-bl-md space-x-1"
                style="background: linear-gradient(135deg, #e5d9a2 0%, #f0e8be 40%, #f7f2df 50%, #f0e8be 60%, #e5d9a2 100%);"
              >
                <UIcon name="i-mdi-star" class="text-primary" /><div class="text-xs font-semibold text-poivre-600">
                  Extra
                </div>
              </div>
              <div class="flex justify-center items-center h-25 ">
                <img
                  :src="`${$config.public.backendCdnUrl}/u/discounts/${item.logo_path}`"
                  class="max-h-full object-contain border-0"
                >
              </div>
              <div class="flex items-center justify-center text-center text-sm font-medium grow">
                <div class="line-clamp-2">
                  {{ item.title }}
                </div>
              </div>
              <div>
                <UButton label="Voir détails + code promo" variant="soft" class="w-full justify-center" size="xs" @click="openDiscountModal(item)" />
              </div>
            </div>
          </UCarousel>
        </div>

        <DisplayAd
          v-if="baseStore.showAds && baseStore?.platform === 'web' && (!userStore?.user || userStore.user.extra != 1)"
          dfp-ad-unit-path="glouton/accueil"
          :sizes="[[970, 250], [728, 90], [320, 50], [300, 250]]"
          :size-mapping="[[[0, 0], [[320, 50], [300, 250]]], [[768, 0], [[728, 90]]], [[1024, 0], [[970, 250], [728, 90]]]]"
          custom-class="mb-6 mx-4 lg:mx-0"
        />

        <div class="px-4 lg:px-0 pb-2 mb-2 xl:mb-4 lg:border-b lg:border-poivre-100 flex justify-between items-center">
          <h3 class="text-base lg:text-2xl font-semibold lg:font-bold leading-6 text-poivre-900 lg:font-title uppercase lg:normal-case">
            Derniers articles
          </h3>
          <UButton size="sm" label="Visiter le blogue" color="primary" variant="solid" :to="localePath({ name: 'ArticlesPage' })" />
        </div>
        <div class="flex justify-between w-full mb-12">
          <UCarousel
            v-slot="{ item }"
            :items="data?.sections?.recent_blog_posts"
            class="w-full"
            :skip-snaps="false"
            :ui="{
              item: 'sm:basis-2/3 md:basis-1/2 lg:basis-1/3 xl:basis-1/3 ps-0',
              viewport: ' px-4 pb-1 overflow-hidden px-4 lg:px-0',
              container: 'space-x-4 items-stretch px-4',
              dot: 'bg-tertiary-200 data-[state=active]:bg-tertiary-500',
            }"
          >
            <ArticleCard
              class="flex flex-col grow h-full"
              :article="item"
            />
          </UCarousel>
        </div>
      </UContainer>
    </template>
  </UDashboardPanel>
</template>

<style scoped>
@keyframes index-member-extra-shine {
  0% {
    transform: translateX(-100%) skewX(-12deg);
  }
  100% {
    transform: translateX(200%) skewX(-12deg);
  }
}

.index-member-extra-shine {
  animation: index-member-extra-shine 3s ease-in-out infinite;
}
</style>
